import React, { useState, useEffect } from 'react';
import { 
  Card, Space, Select, Button, DatePicker, Input, Tag, Tooltip, 
  Dropdown, Menu, Switch, Slider, Badge, Drawer 
} from 'antd';
import { 
  FilterOutlined, SwapOutlined, ReloadOutlined, 
  SettingOutlined, EyeOutlined, SearchOutlined, ClearOutlined,
  CalendarOutlined, TeamOutlined, BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Search } = Input;

const EnhancedControlPanel = ({
  brands = [],
  selectedBrands = [],
  onBrandsChange,
  dateRange,
  onDateRangeChange,
  onRefresh,
  loading = false,
  isMobile = false,
  // Advanced filtering props
  advisors = [],
  selectedAdvisor,
  onAdvisorChange,
  searchText = '',
  onSearchChange,
  // Comparison features
  comparisonMode = false,
  onComparisonToggle,
  comparisonPeriod = 'previous_month',
  onComparisonPeriodChange,
  isCustomComparison = false,
  comparisonDateRange = null,
  onComparisonDateRangeChange,
  // Metric visibility
  visibleMetrics = [],
  onMetricVisibilityChange,
  availableMetrics = [],
  // Cache controls
  cacheEnabled = true,
  onCacheToggle,
  cacheStats = {},
  // Export and actions
  onExport,
  onQuickActions
}) => {
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);

  // AllBrandsMetrics style filtering
  const handleBrandFilter = (brandIds) => {
    onBrandsChange(brandIds);
  };

  const handleClearFilters = () => {
    onBrandsChange([]);
    onAdvisorChange && onAdvisorChange(null);
    onSearchChange && onSearchChange('');
    onDateRangeChange([dayjs().subtract(30, 'days'), dayjs()]);
  };

  const renderQuickDateButtons = () => (
    <Space wrap>
      <Button 
        size="small"
        onClick={() => onDateRangeChange([dayjs().subtract(7, 'days'), dayjs()])}
      >
        Son 7 Gün
      </Button>
      <Button 
        size="small"
        onClick={() => onDateRangeChange([dayjs().subtract(30, 'days'), dayjs()])}
      >
        Son 30 Gün
      </Button>
      <Button 
        size="small"
        onClick={() => onDateRangeChange([dayjs().subtract(90, 'days'), dayjs()])}
      >
        Son 3 Ay
      </Button>
      <Button 
        size="small"
        onClick={() => onDateRangeChange([dayjs().startOf('month'), dayjs()])}
      >
        Bu Ay
      </Button>
    </Space>
  );

  const renderMetricVisibilitySettings = () => (
    <Card size="small" title="Görünür Metrikler" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {availableMetrics.map(metric => (
          <div key={metric.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{metric.label}</span>
            <Switch 
              size="small"
              checked={visibleMetrics.includes(metric.key)}
              onChange={(checked) => {
                const newMetrics = checked 
                  ? [...visibleMetrics, metric.key]
                  : visibleMetrics.filter(m => m !== metric.key);
                onMetricVisibilityChange(newMetrics);
              }}
            />
          </div>
        ))}
      </Space>
    </Card>
  );

  const renderAdvancedFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {/* Single brand selection */}
      <Card size="small" title="Marka Seçimi">
        <Select
          value={selectedBrands[0]}
          onChange={(value) => handleBrandFilter([value])}
          style={{ width: '100%' }}
          placeholder="Marka seçin"
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {brands.map(brand => (
            <Select.Option key={brand.id} value={brand.id}>
              <Space>
                <span>{brand.name}</span>
                {brand.status && (
                  <Tag size="small" color={brand.status === 'active' ? 'green' : 'orange'}>
                    {brand.status}
                  </Tag>
                )}
              </Space>
            </Select.Option>
          ))}
        </Select>
      </Card>

      {/* Historical comparison */}
      <Card size="small" title="Zaman Karşılaştırması">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Switch 
              checked={comparisonMode}
              onChange={onComparisonToggle}
              size="small"
            />
            <span style={{ fontSize: '12px' }}>Geçmiş Dönem Karşılaştırması</span>
          </div>
          {comparisonMode && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Select
                value={comparisonPeriod}
                onChange={onComparisonPeriodChange}
                style={{ width: '100%' }}
                placeholder="Karşılaştırma dönemi seçin"
              >
                <Select.Option value="previous_week">Önceki Hafta</Select.Option>
                <Select.Option value="previous_month">Önceki Ay</Select.Option>
                <Select.Option value="previous_quarter">Önceki Çeyrek</Select.Option>
                <Select.Option value="previous_year">Önceki Yıl</Select.Option>
                <Select.Option value="same_period_last_year">Geçen Yılın Aynı Dönemi</Select.Option>
                <Select.Option value="custom">📅 Özel Tarih Aralığı</Select.Option>
              </Select>
              
              {isCustomComparison && (
                <div>
                  <div style={{ marginBottom: 8, fontSize: '12px', color: '#666' }}>
                    Karşılaştırma Tarihleri:
                  </div>
                  <RangePicker
                    value={comparisonDateRange}
                    onChange={onComparisonDateRangeChange}
                    style={{ width: '100%' }}
                    size="small"
                    placeholder={['Başlangıç', 'Bitiş']}
                    disabledDate={(current) => {
                      // Disable future dates and dates older than 5 years
                      const today = dayjs().endOf('day');
                      const fiveYearsAgo = dayjs().subtract(5, 'year').startOf('day');
                      return current && (current.isAfter(today) || current.isBefore(fiveYearsAgo));
                    }}
                  />
                  <div style={{ fontSize: '10px', color: '#999', marginTop: 4 }}>
                    Son 5 yıl içinde bir tarih aralığı seçin
                  </div>
                </div>
              )}
            </Space>
          )}
        </Space>
      </Card>

      {/* Advisor filtering - AllBrandsMetrics style */}
      {advisors.length > 0 && (
        <Card size="small" title="Danışman Filtresi">
          <Select
            value={selectedAdvisor}
            onChange={onAdvisorChange}
            style={{ width: '100%' }}
            placeholder="Danışman seçin"
            allowClear
          >
            {advisors.map(advisor => (
              <Select.Option key={advisor.id} value={advisor.id}>
                <Space>
                  <TeamOutlined />
                  {advisor.name}
                  <Badge count={advisor.brandCount} size="small" />
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Card>
      )}

      {/* Text search */}
      <Card size="small" title="Arama">
        <Search
          placeholder="Marka, kampanya veya metrik ara..."
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          onSearch={onSearchChange}
          allowClear
        />
      </Card>

      {/* Date range with quick buttons */}
      <Card size="small" title="Tarih Aralığı">
        <Space direction="vertical" style={{ width: '100%' }}>
          <RangePicker
            value={dateRange}
            onChange={onDateRangeChange}
            style={{ width: '100%' }}
            size="small"
          />
          {renderQuickDateButtons()}
        </Space>
      </Card>

      {/* Cache controls */}
      <Card size="small" title="Performans Ayarları">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Önbellek Kullan</span>
            <Switch 
              checked={cacheEnabled}
              onChange={onCacheToggle}
              size="small"
            />
          </div>
          {cacheStats.total > 0 && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              <div>Önbellekte: {cacheStats.cached} / {cacheStats.total}</div>
              <div>Başarı oranı: %{((cacheStats.cached / cacheStats.total) * 100).toFixed(1)}</div>
            </div>
          )}
        </Space>
      </Card>
    </Space>
  );

  const renderMobileFilters = () => (
    <Drawer
      title="Filtreler"
      placement="bottom"
      height="80vh"
      onClose={() => setFiltersVisible(false)}
      open={filtersVisible}
    >
      {renderAdvancedFilters()}
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Space>
          <Button onClick={handleClearFilters} icon={<ClearOutlined />}>
            Temizle
          </Button>
          <Button type="primary" onClick={() => setFiltersVisible(false)}>
            Uygula
          </Button>
        </Space>
      </div>
    </Drawer>
  );

  const renderDesktopFilters = () => (
    <Dropdown
      overlay={
        <div style={{ 
          backgroundColor: 'white', 
          padding: '16px', 
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          width: '400px',
          maxHeight: '70vh',
          overflowY: 'auto'
        }}>
          {renderAdvancedFilters()}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button size="small" onClick={handleClearFilters} icon={<ClearOutlined />}>
                Temizle
              </Button>
            </Space>
          </div>
        </div>
      }
      trigger={['click']}
      placement="bottomRight"
    >
      <Button icon={<FilterOutlined />}>
        Filtreler
        {(selectedBrands.length > 0 || selectedAdvisor || searchText) && (
          <Badge dot style={{ marginLeft: 4 }} />
        )}
      </Button>
    </Dropdown>
  );

  const activeFiltersCount = [
    selectedBrands.length > 0,
    selectedAdvisor,
    searchText
  ].filter(Boolean).length;

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          {/* Main Controls */}
          <Space wrap>
            {/* Brand Selection */}
            <Select
              value={selectedBrands[0]}
              onChange={(value) => handleBrandFilter([value])}
              style={{ width: isMobile ? 150 : 200 }}
              placeholder="Marka Seçin"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {brands.map(brand => (
                <Select.Option key={brand.id} value={brand.id}>
                  {brand.name}
                </Select.Option>
              ))}
            </Select>

            {/* Date Range */}
            {!isMobile && (
              <RangePicker
                value={dateRange}
                onChange={onDateRangeChange}
                style={{ width: 280 }}
              />
            )}
          </Space>

          {/* Action Buttons */}
          <Space wrap>
            {/* Historical Comparison Toggle */}
            <Tooltip title={comparisonMode ? "Normal görünüme geç" : "Geçmiş dönem karşılaştırması"}>
              <Button 
                type={comparisonMode ? "primary" : "default"}
                icon={<SwapOutlined />}
                onClick={onComparisonToggle}
              >
                {isMobile ? "" : comparisonMode ? "Normal Görünüm" : "Karşılaştır"}
              </Button>
            </Tooltip>

            {/* Refresh */}
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={onRefresh}
            >
              {!isMobile && "Yenile"}
            </Button>
          </Space>
        </Space>

        {/* Comparison Settings - Only show when comparison mode is active */}
        {comparisonMode && (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
            <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
              <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Karşılaştırma Ayarları:</div>
              
              <Select
                value={comparisonPeriod}
                onChange={onComparisonPeriodChange}
                style={{ width: isMobile ? '100%' : 200 }}
                size="small"
              >
                <Select.Option value="previous_week">Önceki Hafta</Select.Option>
                <Select.Option value="previous_month">Önceki Ay</Select.Option>
                <Select.Option value="previous_quarter">Önceki Çeyrek</Select.Option>
                <Select.Option value="previous_year">Önceki Yıl</Select.Option>
                <Select.Option value="custom">📅 Özel Tarih</Select.Option>
              </Select>
              
              {isCustomComparison && (
                <RangePicker
                  value={comparisonDateRange}
                  onChange={onComparisonDateRangeChange}
                  style={{ width: isMobile ? '100%' : 280 }}
                  size="small"
                  placeholder={['Başlangıç', 'Bitiş']}
                  disabledDate={(current) => {
                    const today = dayjs().endOf('day');
                    const fiveYearsAgo = dayjs().subtract(5, 'year').startOf('day');
                    return current && (current.isAfter(today) || current.isBefore(fiveYearsAgo));
                  }}
                />
              )}
            </Space>
          </div>
        )}
      </Card>

      {/* Mobile Date Picker */}
      {isMobile && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Tarih Aralığı:</div>
            <RangePicker
              value={dateRange}
              onChange={onDateRangeChange}
              style={{ width: '100%' }}
              size="small"
            />
            {renderQuickDateButtons()}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default React.memo(EnhancedControlPanel);